import {
  AdvancedSearchComponentType,
  SearchParams,
} from '@prismamedia/one-components';
import React, { FC, Fragment, useMemo } from 'react';
import { MultimediaList } from '../MultimediaList';
import {
  MultiMediaListItemType,
  MultimediaListProps,
} from '../MultimediaList/types';
import { MultimediaSearch } from '../MultimediaSearch';
import { MultiMediaFetchItemsType, ViewMode } from '../types';
import { getMultiMediaListHeaderTitleText } from '../utils';
import { ViewMode as MemoizedViewMode } from './ViewMode.memo';

interface DefaultMediaContentProps extends MultimediaListProps {
  allowSearch: boolean;
  multimediaSearchProps: {
    onViewModeChange: (viewMode: ViewMode) => void;
    placeholder?: string;
    searchParams: SearchParams;
    setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
    advancedSearch?: AdvancedSearchComponentType;
    fetchItems: MultiMediaFetchItemsType;
  };
}

export const DefaultMediaContent: FC<DefaultMediaContentProps> = ({
  allowSearch,
  isLoading,
  items,
  suggestedItems,
  multimediaSearchProps: {
    fetchItems,
    onViewModeChange,
    placeholder,
    advancedSearch,
    searchParams,
    setSearchParams,
  },
  pluginType,
  viewMode,
  ...restDefaultMediaContentProps
}) => {
  /**
   * We inject the title(s) in the item list, and we handle the rendering
   * of these titles in the GridView and the ListView
   * This is because we need to be able to split the view by headings
   */
  const itemsWithSuggestionsAndTitle = useMemo(
    () => {
      return [
        ...(suggestedItems?.length
          ? [
              {
                type: MultiMediaListItemType.HEADING,
                text: 'Suggestions',
                height: 25,
              },
              ...suggestedItems.map((item) => ({ ...item, isSuggested: true })),
            ]
          : []),
        {
          type: MultiMediaListItemType.HEADING,
          height: 25,
          text:
            Object.keys(searchParams).length > 1 || searchParams?.search?.length
              ? `RESULTATS ${items.length ? items.length : '...'}`
              : getMultiMediaListHeaderTitleText(pluginType),
        },
        ...items,
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestedItems, items],
  );

  return (
    <Fragment>
      {/* Header search bar and popup content title */}
      {allowSearch && (
        <>
          <MultimediaSearch
            displayMode={
              <MemoizedViewMode
                onViewModeChange={onViewModeChange}
                viewMode={viewMode}
              />
            }
            pluginType={pluginType}
            fetchItems={fetchItems}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            advancedSearch={advancedSearch}
            placeholder={placeholder}
          />
        </>
      )}

      <MultimediaList
        allowSearch={allowSearch}
        items={itemsWithSuggestionsAndTitle}
        isLoading={isLoading}
        pluginType={pluginType}
        viewMode={viewMode}
        {...restDefaultMediaContentProps}
      />
    </Fragment>
  );
};
